import * as React from "react";

import { FormField } from "@/components/ui/form-field";
import { Input } from "@/components/ui/input";
import { SearchableCombobox } from "@/components/ui/searchable-combobox";
import { StatsGrid, type StatTile } from "@/components/ui/stats-grid";
import { formatDate } from "@/lib/format-date";
import type { AdminOrgRow } from "@/hooks/queries/admin-orgs-types";
import type {
  AdminUserEntitlementBlock,
  AdminUserSubscriptionBlock,
} from "@/hooks/queries/admin-users-types";
import { DangerConfirmDialog } from "@/routes/admin/DangerConfirmDialog";
import { subscriptionPill } from "@/routes/admin/admin-users-derivations";
import {
  attachOrgOptions,
  seatImpactAfterAttach,
} from "@/routes/admin/attach-org-options";

// Story 22.1 (FR88) — Screen 39 Panel 1, on the shipped EnablePaidAccountDialog
// shape: `DangerConfirmDialog` with `mustReadVariant="info"` and
// `confirmVariant="primary"`. The attach is not destructive to the ACCOUNT, and
// dressing it as destruction miscommunicates what the administrator is doing.
//
// Four editable fields and THREE READ-ONLY BLOCKS. The read-only half is not
// decoration: it is the only place a super_admin sees, before they confirm, what
// the client's ruling costs this particular person — which contract end date
// they are adopting, what it does to the organization's seat pool, and what
// individual entitlement they are giving up to adopt it.
//
// There is deliberately NO Reason / note field. §4D's field table carries no
// such row, AC22 names four editable fields, and AC20 pins an attach audit
// payload that carries no note. Whether the attach should carry one AT ALL is a
// UX question carried forward, not a field to add on a drawing's authority.

/** Screen 34's five options, unchanged — display labels only (FR75). */
export const ORG_ROLE_LABEL_OPTIONS = [
  "Clinician",
  "Clinical director",
  "OT manager",
  "Program lead",
  "Supervisor",
] as const;

/**
 * FR83, stated with NO DURATION AND NO MECHANISM (boundary row 20).
 *
 * Not "permanently" and not "this cannot be undone" — Epic 22's correction
 * window falsifies both two units later. Not "unless CITS reverses it within
 * seven days" either: at this story's merge the reversal does not exist, and the
 * window's length is an environment setting this screen cannot read, so the
 * dialog would be stating a number it has no way to verify.
 */
export const SEAT_CONSUMPTION_SENTENCE =
  "This seat counts against the organization's capacity for the contract term.";

export const NO_REFUND_SENTENCE =
  "The unused remainder of an already-paid period is not refunded or credited.";

/**
 * The two statuses from which no money can move and no Stripe cancellation is
 * possible, because the subscription is already over.
 *
 * A MIRROR of the server's `TERMINAL_SUBSCRIPTION_STATUSES`
 * (`src/application/billing/_shared/models.py`, re-exported through
 * `billing/contract.py`). THE TWO MUST MOVE TOGETHER: a status added to or
 * removed from that tuple is a change to this list. Mirrored rather than
 * imported for the reason `LIVE_SUBSCRIPTION_STATUSES` already is in
 * `admin-users-derivations.ts` — this file is a rendering vocabulary and the
 * server's is a money predicate, and the only thing they share is the list.
 *
 * MIRRORED BY SUBTRACTION, exactly as the server derives
 * `CANCELLABLE_SUBSCRIPTION_STATUSES` from this pair, and deliberately NOT as a
 * third enumerated list of cancellable statuses: a status added to the
 * lifecycle later is then reported as cancellable BY DEFAULT. The two
 * directions are not symmetric — over-reporting names a cancellation that turns
 * out to have been a no-op, while under-reporting is the §1.5 failure the
 * must-read block exists to prevent.
 */
export const TERMINAL_SUBSCRIPTION_STATUSES: readonly string[] = [
  "canceled",
  "incomplete_expired",
];

/**
 * Whether the attach will cancel this billing row — the client-side mirror of
 * the server's `CANCELLABLE_SUBSCRIPTION_STATUSES`, which is what
 * `prepare_subscription_cancellation` actually collects on this path.
 *
 * NOT `hasLiveSubscriptionRow`. That predicate mirrors
 * `LIVE_SUBSCRIPTION_STATUSES`, which answers a WRITE-time question — which row
 * occupies the one-live-subscription-per-user slot — and deliberately excludes
 * `past_due`, `unpaid`, `incomplete` and `paused` so a failed payment does not
 * block a fresh checkout. The attach asks a different question: which Stripe
 * object am I about to cancel. A clinician in dunning holds exactly such an
 * object, and keying the must-read on the live set told the administrator "no
 * billing at all" before cancelling it anyway.
 *
 * REPORTING ONLY. Nothing here authorises or suppresses anything; the server
 * cancels what it cancels regardless of what this block says.
 */
export function hasCancellableSubscriptionRow(
  subscription?: AdminUserSubscriptionBlock,
): boolean {
  const status = subscription?.status;
  return status != null && !TERMINAL_SUBSCRIPTION_STATUSES.includes(status);
}

export interface AttachToOrganizationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userName: string;
  /** Professional and personal organizations alike; the picker filters. */
  orgs: AdminOrgRow[];
  orgsLoading?: boolean;
  /** How many organizations the server says exist, for the truncation note. */
  orgsTotal?: number;
  entitlement?: AdminUserEntitlementBlock;
  subscription?: AdminUserSubscriptionBlock;
  isPending: boolean;
  onConfirm: (values: {
    organization_id: string;
    organization_role_label: string;
    department?: string;
    hr_id?: string;
  }) => void;
  /** Injectable clock — the contract-in-force test is date-dependent. */
  now?: Date;
}

export function AttachToOrganizationDialog({
  open,
  onOpenChange,
  userName,
  orgs,
  orgsLoading = false,
  orgsTotal,
  entitlement,
  subscription,
  isPending,
  onConfirm,
  now,
}: AttachToOrganizationDialogProps) {
  const [organizationId, setOrganizationId] = React.useState("");
  const [roleLabel, setRoleLabel] = React.useState<string>(ORG_ROLE_LABEL_OPTIONS[0]);
  const [department, setDepartment] = React.useState("");
  const [hrId, setHrId] = React.useState("");

  // Every re-open starts from the contract's defaults. Without this, an
  // administrator who cancels an attach to one organization and opens the dialog
  // on another arrives with the first one's department and HR id already filled.
  React.useEffect(() => {
    if (!open) return;
    setOrganizationId("");
    setRoleLabel(ORG_ROLE_LABEL_OPTIONS[0]);
    setDepartment("");
    setHrId("");
  }, [open]);

  const clock = now ?? new Date();
  const options = React.useMemo(() => attachOrgOptions(orgs, clock), [orgs, clock]);
  const selected = orgs.find((org) => org.id === organizationId);
  const impact = selected ? seatImpactAfterAttach(selected) : null;

  const seatTiles: StatTile[] = impact
    ? [
        { label: "Seat capacity", value: impact.seatCapacity, foot: "Unchanged" },
        {
          label: "Assigned",
          value: impact.assigned,
          foot: `+1 — was ${impact.assigned - 1}`,
        },
        {
          label: "Active seats",
          value: impact.activeSeats ?? "—",
          foot:
            impact.activeSeats == null
              ? "Not reported"
              : `+1 — was ${impact.activeSeats - 1}`,
        },
        {
          label: "Available",
          value: impact.available,
          foot: `−1 — was ${impact.available + 1}`,
        },
      ]
    : [];

  const truncated = orgsTotal != null && orgsTotal > orgs.length;

  return (
    <DangerConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Attach to organization"
      description={`${userName} will be placed on an organization's roster and will hold one of its seats.`}
      mustRead={
        <EntitlementsToEnd
          userName={userName}
          entitlement={entitlement}
          subscription={subscription}
        />
      }
      mustReadVariant="info"
      confirmVariant="primary"
      confirmLabel="Attach clinician"
      pendingLabel="Attaching…"
      isPending={isPending}
      // Organization is the one required field with no default (AC22). Without
      // this the administrator can confirm an empty picker and the client builds
      // `/admin/orgs//attach-clinician` — a 404 that says nothing about the
      // field they did not fill in.
      confirmDisabled={!organizationId}
      onConfirm={() =>
        onConfirm({
          organization_id: organizationId,
          organization_role_label: roleLabel,
          department: department.trim() || undefined,
          hr_id: hrId.trim() || undefined,
        })
      }
    >
      <div className="grid gap-4">
        <FormField
          label="Organization"
          htmlFor="attach-organization"
          required
          helper={
            <>
              Professional organizations only — a personal organization is never a
              seat host. An organization that cannot take this clinician today is
              shown with its reason and cannot be chosen.
              {truncated
                ? ` Showing ${orgs.length} of ${orgsTotal}; search by name to reach the rest.`
                : ""}
            </>
          }
        >
          <SearchableCombobox
            id="attach-organization"
            options={options}
            value={organizationId}
            onChange={setOrganizationId}
            placeholder={orgsLoading ? "Loading organizations…" : "Select an organization…"}
            searchAriaLabel="Search organizations"
            clearAriaLabel="Clear organization"
            disabled={orgsLoading}
          />
        </FormField>

        <div className="grid gap-4 md:grid-cols-2">
          <FormField
            label="Org role label"
            htmlFor="attach-role-label"
            required
            helper="Display label only — every option resolves to the single clinician permission set."
          >
            <SearchableCombobox
              id="attach-role-label"
              options={ORG_ROLE_LABEL_OPTIONS.map((o) => ({ value: o, label: o }))}
              value={roleLabel}
              onChange={setRoleLabel}
              searchAriaLabel="Org role label"
              showSearch={false}
            />
          </FormField>

          <FormField
            label="Contract end date this clinician is adopting"
            htmlFor="attach-contract-end"
            helper="Read-only. Their access will end when the organization's contract term does."
          >
            <Input
              id="attach-contract-end"
              readOnly
              className="[font-variant-numeric:tabular-nums]"
              value={selected ? formatDate(selected.contract_end_date) : "—"}
            />
          </FormField>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <FormField label="Department (optional)" htmlFor="attach-department">
            <Input
              id="attach-department"
              placeholder="e.g. Inpatient OT, Site B"
              value={department}
              onChange={(event) => setDepartment(event.target.value)}
            />
          </FormField>
          <FormField label="HR ID (optional)" htmlFor="attach-hr-id">
            <Input
              id="attach-hr-id"
              placeholder="Internal reference"
              value={hrId}
              onChange={(event) => setHrId(event.target.value)}
            />
          </FormField>
        </div>

        {/* Not a FormField: there is no control here to label, and a <label
            htmlFor> pointing at a <dl> names nothing. */}
        <section aria-labelledby="attach-seat-impact" className="grid gap-[6px]">
          <h3
            id="attach-seat-impact"
            className="text-sm font-medium text-[color:var(--text-secondary)]"
          >
            Seat impact — after this attach
          </h3>
          {impact ? (
            <StatsGrid stats={seatTiles} />
          ) : (
            <p className="text-sm text-[color:var(--text-tertiary)]">
              Choose an organization to see what this attach does to its seats.
            </p>
          )}
          <p className="text-sm text-[color:var(--text-tertiary)]">
            {SEAT_CONSUMPTION_SENTENCE}
          </p>
        </section>
      </div>
    </DangerConfirmDialog>
  );
}

/**
 * The two individual entitlements an attach ends, named SEPARATELY.
 *
 * A manual override and a Stripe subscription are independent — a clinician on a
 * LearnWorlds bundled year holds the first and not the second — so reporting one
 * number, or one sentence, is exactly the case the SCP's §1.5 exists to prevent:
 * the administrator confirms an attach believing they are ending the thing they
 * can see, and ends another they cannot.
 *
 * When there is neither, this says so PLAINLY rather than leaving a blank that
 * implies a cancellation happened.
 */
function EntitlementsToEnd({
  userName,
  entitlement,
  subscription,
}: {
  userName: string;
  entitlement?: AdminUserEntitlementBlock;
  subscription?: AdminUserSubscriptionBlock;
}) {
  const override = entitlement?.source === "manual_override" ? entitlement : null;
  const cancellableSubscription = hasCancellableSubscriptionRow(subscription)
    ? subscription
    : null;

  return (
    <div className="grid gap-2">
      <strong>What this ends for {userName}.</strong>
      {override || cancellableSubscription ? (
        <>
          <ul className="grid gap-1 pl-4 [list-style:disc]">
            {override ? (
              <li>
                <strong>Manual override</strong> — active
                {override.valid_until
                  ? `, ends ${formatDate(override.valid_until)}`
                  : ", no end date"}
                . Revoked on attach.
              </li>
            ) : (
              <li>
                <strong>Manual override</strong> — none on this account.
              </li>
            )}
            {cancellableSubscription ? (
              <li>
                {/* Screen 21's shipped status vocabulary, not the wire value:
                    now that the dunning statuses reach this line, a raw
                    `past_due` would be the first place on this platform an
                    administrator is asked to read one. */}
                <strong>Individual subscription</strong> —{" "}
                {subscriptionPill(cancellableSubscription).label}
                {cancellableSubscription.current_period_end
                  ? `, current period ends ${formatDate(cancellableSubscription.current_period_end)}`
                  : ""}
                . Cancelled on attach.
              </li>
            ) : (
              <li>
                <strong>Individual subscription</strong> — none billing on this
                account.
              </li>
            )}
          </ul>
          <p>{NO_REFUND_SENTENCE}</p>
        </>
      ) : (
        <p>
          This clinician has neither a live manual override nor a cancellable
          subscription, so this attach ends no individual entitlement.
        </p>
      )}
    </div>
  );
}
